import React, { useState, useEffect } from "react";
import { resourcesAPI, rolesAPI } from "@/services/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Users, Plus, Pencil, Trash2, Search, Mail, Loader2, Shield } from "lucide-react";

const STANDARD_ROLES = ["Management", "OSR", "ISR", "Project Management", "Engineering", "Supply Chain", "Quality"];
const MANAGEMENT_ROLES = ["Management", "Project Management"];

const ROLE_COLORS = {
  "Management": "bg-purple-500",
  "Project Management": "bg-purple-400",
  "OSR": "bg-blue-500",
  "ISR": "bg-green-500",
  "Engineering": "bg-orange-500",
  "Supply Chain": "bg-cyan-500",
  "Quality": "bg-pink-500",
};

function TeamPage() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRolesDialog, setShowRolesDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [form, setForm] = useState({ name: "", email: "", roles: ["ISR"] });
  const [selectedRoles, setSelectedRoles] = useState([]);

  useEffect(() => {
    loadResources();
  }, []);

  async function loadResources() {
    try {
      setLoading(true);
      const response = await resourcesAPI.getAll();
      if (response.data.success) {
        setResources(response.data.data);
      }
    } catch (e) {
      toast.error("Failed to load team members");
    } finally {
      setLoading(false);
    }
  }

  function openAddDialog() {
    setForm({ name: "", email: "", roles: ["ISR"] });
    setShowAddDialog(true);
  }

  function openEditDialog(member) {
    setSelectedMember(member);
    setForm({
      name: member.fields?.name || "",
      email: member.fields?.email || "",
      roles: member.fields?.roles || []
    });
    setShowEditDialog(true);
  }

  function openRolesDialog(member) {
    setSelectedMember(member);
    setSelectedRoles(member.fields?.roles || []);
    setShowRolesDialog(true);
  }

  function openDeleteDialog(member) {
    setSelectedMember(member);
    setShowDeleteDialog(true);
  }

  function toggleRole(role) {
    setSelectedRoles(prev => 
      prev.includes(role) 
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  }

  function toggleFormRole(role) {
    setForm(prev => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role]
    }));
  }

  async function handleAdd() {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (form.roles.length === 0) {
      toast.error("At least one role is required");
      return;
    }
    
    try {
      setSaving(true);
      await resourcesAPI.create(form);
      toast.success("Team member added");
      setShowAddDialog(false);
      loadResources();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add team member");
    } finally {
      setSaving(false);
    }
  }

  async function handleEdit() {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (form.roles.length === 0) {
      toast.error("At least one role is required");
      return;
    }
    
    try {
      setSaving(true);
      await resourcesAPI.update(selectedMember.id, form);
      toast.success("Team member updated");
      setShowEditDialog(false);
      loadResources();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update team member");
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdateRoles() {
    if (selectedRoles.length === 0) {
      toast.error("At least one role is required");
      return;
    }
    
    try {
      setSaving(true);
      await resourcesAPI.updateRoles(selectedMember.id, selectedRoles);
      toast.success("Roles updated successfully");
      setShowRolesDialog(false);
      loadResources();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update roles");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    try {
      setSaving(true);
      await resourcesAPI.delete(selectedMember.id);
      toast.success("Team member deleted");
      setShowDeleteDialog(false);
      loadResources();
    } catch (e) {
      toast.error("Failed to delete team member");
    } finally {
      setSaving(false);
    }
  }

  // Filter resources
  const filteredResources = resources.filter(r => {
    const name = r.fields?.name || "";
    const email = r.fields?.email || "";
    const roles = r.fields?.roles || [];
    
    const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === "all" || roles.includes(roleFilter);
    
    return matchesSearch && matchesRole;
  });

  // Stats
  const totalMembers = resources.length;
  const managementCount = resources.filter(r => 
    (r.fields?.roles || []).some(role => MANAGEMENT_ROLES.includes(role))
  ).length;
  const osrCount = resources.filter(r => (r.fields?.roles || []).includes("OSR")).length;
  const isrCount = resources.filter(r => (r.fields?.roles || []).includes("ISR")).length;

  // Role checkbox component
  const RoleCheckboxGroup = ({ selectedRoles, onToggle, disabled }) => (
    <div className="grid grid-cols-2 gap-3">
      {STANDARD_ROLES.map(role => (
        <div key={role} className="flex items-center space-x-2">
          <Checkbox
            id={`role-${role}`}
            checked={selectedRoles.includes(role)}
            onCheckedChange={() => onToggle(role)}
            disabled={disabled}
          />
          <Label 
            htmlFor={`role-${role}`} 
            className={`text-sm cursor-pointer ${MANAGEMENT_ROLES.includes(role) ? 'font-semibold' : ''}`}
          >
            {role}
            {MANAGEMENT_ROLES.includes(role) && (
              <Shield className="inline-block ml-1 h-3 w-3 text-purple-500" />
            )}
          </Label>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Team Management</h1>
          <p className="text-muted-foreground">Manage team members and their roles</p>
        </div>
        <Button onClick={openAddDialog} data-testid="add-member-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Team Member
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Members</p>
                <p className="text-2xl font-bold">{totalMembers}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Management</p>
                <p className="text-2xl font-bold">{managementCount}</p>
              </div>
              <Shield className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div>
              <p className="text-sm text-muted-foreground">OSR</p>
              <p className="text-2xl font-bold">{osrCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div>
              <p className="text-sm text-muted-foreground">ISR</p>
              <p className="text-2xl font-bold">{isrCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px] max-w-sm">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-48">
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {STANDARD_ROLES.map(role => (
                    <SelectItem key={role} value={role}>{role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Team Table */}
      <Card>
        <CardHeader>
          <CardTitle>Team Members ({filteredResources.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredResources.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No team members found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredResources.map((member) => {
                  const roles = member.fields?.roles || [];
                  const isManagement = roles.some(r => MANAGEMENT_ROLES.includes(r));
                  
                  return (
                    <TableRow key={member.id}>
                      <TableCell className="font-medium">
                        {member.fields?.name || "Unknown"}
                        {isManagement && (
                          <Shield className="inline-block ml-2 h-4 w-4 text-purple-500" />
                        )}
                      </TableCell>
                      <TableCell>
                        {member.fields?.email ? (
                          <a href={`mailto:${member.fields.email}`} className="flex items-center text-blue-600 hover:underline">
                            <Mail className="h-4 w-4 mr-1" />
                            {member.fields.email}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {roles.length > 0 ? (
                            roles.map(role => (
                              <Badge 
                                key={role} 
                                className={`${ROLE_COLORS[role] || 'bg-slate-500'} text-white text-xs`}
                              >
                                {role}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-muted-foreground text-sm">No roles assigned</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openRolesDialog(member)}
                            data-testid={`edit-roles-${member.id}`}
                          >
                            <Shield className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openEditDialog(member)}
                            data-testid={`edit-${member.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openDeleteDialog(member)}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-${member.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>Add a new team member with their roles</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Enter email"
              />
            </div>
            <div className="space-y-2">
              <Label>Roles *</Label>
              <p className="text-xs text-muted-foreground mb-2">
                <Shield className="inline-block h-3 w-3 mr-1" />
                indicates management access
              </p>
              <RoleCheckboxGroup 
                selectedRoles={form.roles} 
                onToggle={toggleFormRole}
                disabled={saving}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
            <DialogDescription>Update team member details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name *</Label>
              <Input
                id="edit-name"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Enter email"
              />
            </div>
            <div className="space-y-2">
              <Label>Roles *</Label>
              <p className="text-xs text-muted-foreground mb-2">
                <Shield className="inline-block h-3 w-3 mr-1" />
                indicates management access
              </p>
              <RoleCheckboxGroup 
                selectedRoles={form.roles} 
                onToggle={toggleFormRole}
                disabled={saving}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleEdit} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Roles Dialog */}
      <Dialog open={showRolesDialog} onOpenChange={setShowRolesDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Roles</DialogTitle>
            <DialogDescription>
              Update roles for {selectedMember?.fields?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-xs text-muted-foreground mb-4">
              <Shield className="inline-block h-3 w-3 mr-1" />
              indicates management access (can edit projects, tasks, and team)
            </p>
            <RoleCheckboxGroup 
              selectedRoles={selectedRoles} 
              onToggle={toggleRole}
              disabled={saving}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRolesDialog(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleUpdateRoles} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Update Roles
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Team Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedMember?.fields?.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default TeamPage;
